import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Check, Eye, Pencil, X as XIcon, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { formatRequestAmount } from "./AdminTransactions";
import useAdminAutoRefresh from "../../lib/useAdminAutoRefresh";
import { VerifiedName } from "../../components/VerifiedBadge";

const TYPE_TONES = {
  load: "bg-emerald-900/30 border-emerald-700/40 text-emerald-300",
  redeem: "bg-amber-900/30 border-amber-700/40 text-amber-300",
  admin_credit: "bg-emerald-900/30 border-emerald-700/40 text-emerald-300",
  admin_debit: "bg-amber-900/30 border-amber-700/40 text-amber-300",
  create_account: "bg-sky-900/30 border-sky-700/40 text-sky-300",
  reset_password: "bg-violet-900/30 border-violet-700/40 text-violet-300",
  identity_verification: "bg-cyan-900/30 border-cyan-700/40 text-cyan-300",
};
const STATUS_TONES = {
  pending: "bg-slate-800 border-white/10 text-slate-300",
  approved: "bg-emerald-900/30 border-emerald-700/40 text-emerald-300",
  rejected: "bg-red-900/30 border-red-700/40 text-red-300",
};

export default function AdminRequests() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState({});
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    const q = filter === "all" ? "" : `?status_filter=${filter}`;
    const { data } = await api.get(`/admin/requests${q}`);
    setRequests(data);
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useAdminAutoRefresh(() => load());

  const act = async (req, status) => {
    if (busy[req.id]) return;
    let note = "";
    if (status === "approved" && req.type === "create_account") {
      note = promptAccountDetails(req.admin_note);
      if (!note) return;
    } else if (status === "approved" && req.type === "reset_password") {
      note = req.admin_note || "";
    } else if (status === "approved" && req.type === "identity_verification") {
      note = "ID front, ID back, and selfie approved";
    } else if (status === "rejected" && req.type === "identity_verification") {
      note = window.prompt("Reason for rejecting ID/selfie:", req.admin_note || "Please upload clear ID front, ID back, and selfie photos.") || "";
      if (!note.trim()) return;
    }
    setBusy((b) => ({ ...b, [req.id]: true }));
    try {
      await api.put(`/admin/requests/${req.id}`, { status, admin_note: note });
      toast.success(`Request ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [req.id]: false }));
    }
  };

  const editDetails = async (req) => {
    if (busy[req.id]) return;
    const note = req.type === "create_account"
      ? promptAccountDetails(req.admin_note)
      : promptPasswordDetails(req.admin_note);
    if (!note) return;
    setBusy((b) => ({ ...b, [req.id]: true }));
    try {
      await api.put(`/admin/requests/${req.id}/details`, { admin_note: note });
      toast.success("Details updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [req.id]: false }));
    }
  };

  return (
    <div className="p-3 sm:p-6 md:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Player requests</h1>
          <p className="text-sm text-slate-500 mt-1">{requests.length} {filter !== "all" && `(${filter})`}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="refresh-requests"
            className="p-2 rounded-md hover:bg-white/5 border border-white/10">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          {["pending", "approved", "rejected", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-3 py-1.5 text-xs rounded-md border capitalize ${
                filter === f ? "bg-white text-black border-white" : "border-white/10 text-slate-300 hover:bg-white/5"
              }`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-white/10 bg-[#121214]">
        <table className="min-w-[1080px] w-full text-sm">
          <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Note</th>
              <th className="px-4 py-3">Files</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} data-testid={`request-row-${r.id}`} className="border-t border-white/5">
                <td className="px-4 py-3">
                  <div className="font-medium"><VerifiedName name={r.username} user={r} /></div>
                  <div className="text-xs text-slate-500">{r.email}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-xs rounded-md border ${TYPE_TONES[r.type] || ""}`}>
                    {typeLabel(r.type)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{formatRequestAmount(r, "—")}</td>
                <td className="px-4 py-3 text-slate-400 text-xs max-w-xs truncate">{r.note || "—"}</td>
                <td className="px-4 py-3">
                  {r.type === "identity_verification" && (r.identity_id_image_url || r.identity_id_back_image_url || r.identity_selfie_image_url) ? (
                    <button
                      type="button"
                      onClick={() => setPreview(r)}
                      className="inline-flex items-center gap-1 rounded-md border border-[#2F6FA5]/30 px-2 py-1 text-xs font-semibold text-[#8FB7E1] hover:bg-[#2F6FA5]/10"
                    >
                      <Eye className="h-3.5 w-3.5" />
                      View ID
                    </button>
                  ) : (
                    <span className="text-xs text-slate-600">-</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-xs rounded-md border ${STATUS_TONES[r.status] || ""}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {r.status === "pending" ? (
                    <div className="flex gap-1 justify-end">
                      <button
                        onClick={() => act(r, "approved")}
                        disabled={busy[r.id]}
                        data-testid={`approve-${r.id}`}
                        className="p-1.5 rounded hover:bg-emerald-500/15 text-emerald-300 border border-emerald-700/30"
                        title="Approve"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => act(r, "rejected")}
                        disabled={busy[r.id]}
                        data-testid={`reject-${r.id}`}
                        className="p-1.5 rounded hover:bg-red-500/15 text-red-300 border border-red-700/30"
                        title="Reject"
                      >
                        <XIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-end gap-2">
                      <span className="block max-w-xs text-xs text-slate-500 italic whitespace-pre-wrap text-left">{r.admin_note || "—"}</span>
                      {["create_account", "reset_password"].includes(r.type) && (
                        <button
                          onClick={() => editDetails(r)}
                          disabled={busy[r.id]}
                          data-testid={`edit-details-${r.id}`}
                          className="p-1.5 rounded hover:bg-white/10 text-slate-300 border border-white/10 shrink-0"
                          title="Edit details"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {requests.length === 0 && (
              <tr><td colSpan={8} className="text-center text-slate-500 py-10">No requests in this category.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {preview && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/75 p-4 backdrop-blur-sm" onClick={() => setPreview(null)}>
          <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-2xl border border-white/10 bg-[#121214] p-4 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">ID & selfie verification</h2>
                <p className="text-xs text-slate-500">{preview.username || preview.email} - {preview.email || preview.user_id}</p>
                <p className="mt-1 text-sm text-slate-300">Full name: <span className="font-semibold text-white">{preview.full_name || "-"}</span></p>
                <p className="mt-1 text-sm text-slate-300">DOB / State: <span className="font-semibold text-white">{preview.date_of_birth || "-"} / {preview.state || "-"}</span></p>
              </div>
              <button onClick={() => setPreview(null)} className="rounded-md border border-white/10 p-2 text-slate-300 hover:bg-white/5">
                <XIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <IdentityImage label="ID front" name={preview.identity_id_image_name} src={preview.identity_id_image_url} />
              <IdentityImage label="ID back" name={preview.identity_id_back_image_name} src={preview.identity_id_back_image_url} />
              <IdentityImage label="Selfie" name={preview.identity_selfie_image_name} src={preview.identity_selfie_image_url} />
            </div>
            <DuplicateRisk flags={preview.identity_duplicate_flags} />
          </div>
        </div>
      )}
    </div>
  );
}

function DuplicateRisk({ flags }) {
  const rows = Array.isArray(flags) ? flags : [];
  if (!rows.length) return null;
  return (
    <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
      <div className="text-sm font-bold text-amber-200">Possible duplicate account</div>
      <div className="mt-2 space-y-2">
        {rows.slice(0, 5).map((flag) => (
          <div key={flag.user_id || flag.email} className="rounded-lg border border-amber-500/20 bg-black/20 px-3 py-2 text-xs text-amber-100">
            <div className="font-semibold">{flag.username || flag.email || flag.user_id || "Matched account"}</div>
            <div className="mt-1 text-amber-200/80">{(flag.matches || []).join(", ")}</div>
            {flag.identity_status && <div className="mt-1 text-amber-200/60">Status: {String(flag.identity_status).replace(/_/g, " ")}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function IdentityImage({ label, name, src }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-3">
      <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
      {src ? (
        <a href={src} target="_blank" rel="noreferrer" className="block">
          <img src={src} alt={name || label} className="max-h-[68vh] w-full rounded-lg bg-black object-contain" />
          <div className="mt-2 truncate text-xs text-[#8FB7E1]">{name || "Open full image"}</div>
        </a>
      ) : (
        <div className="grid h-64 place-items-center rounded-lg border border-dashed border-white/10 text-sm text-slate-500">
          Not submitted
        </div>
      )}
    </div>
  );
}

function typeLabel(type) {
  return (type || "").replace(/_/g, " ");
}

function promptAccountDetails(existing = "") {
  const current = parseDetails(existing);
  const username = window.prompt("Game username to show customer:", current.username || "");
  if (username === null) return "";
  if (!username.trim()) {
    toast.error("Username is required");
    return "";
  }
  const password = window.prompt("Game password to show customer:", current.password || "");
  if (password === null) return "";
  if (!password.trim()) {
    toast.error("Password is required");
    return "";
  }
  const notes = window.prompt("Extra notes (optional):", current.notes || "");
  if (notes === null) return "";
  return [
    `Username: ${username.trim()}`,
    `Password: ${password.trim()}`,
    notes.trim() ? `Notes: ${notes.trim()}` : "",
  ].filter(Boolean).join("\n");
}

function promptPasswordDetails(existing = "") {
  const current = parseDetails(existing);
  const password = window.prompt("New password to show customer:", current.password || existing || "");
  if (password === null) return "";
  if (!password.trim()) {
    toast.error("New password is required");
    return "";
  }
  return `Password: ${password.trim()}`;
}

function parseDetails(text = "") {
  const out = {};
  text.split(/\r?\n/).forEach((line) => {
    const [rawKey, ...rest] = line.split(":");
    const key = rawKey.trim().toLowerCase();
    const value = rest.join(":").trim();
    if (!value) return;
    if (["username", "user", "login"].includes(key)) out.username = value;
    if (["password", "pass", "new password"].includes(key)) out.password = value;
    if (["notes", "note"].includes(key)) out.notes = value;
  });
  return out;
}
